import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { PhoneAuthProvider, RecaptchaVerifier, signOut, updatePhoneNumber, updateProfile } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import BottomNav from "../components/BottomNav";
import IOSThemePreference from "../components/IOSThemePreference";
import PassengerAvatar from "../components/PassengerAvatar";
import { getHistoricoCorridas } from "../services/rideService";
import { getDescontoDisponivel } from "../services/discountService";
import { LuAccessibility, LuBriefcaseBusiness, LuCarFront, LuCheck, LuCircleAlert, LuContrast, LuHouse, LuMail, LuMapPin, LuMessageCircle, LuPencil, LuPhone, LuRoute, LuShieldCheck, LuStar, LuTags, LuTicketPercent, LuType, LuUserRound, LuX, LuZap } from "react-icons/lu";
import logoZuno from "../assets/zuno-splash.png";
import { getCachedProfilePhoto, setCachedProfilePhoto } from "../services/profileStore";
import { useAccessibility } from "../accessibility/AccessibilityContext";
import { limparTokenNotificacoes } from "../services/notificationService";
import { getLegalReadiness, updateMarketingPreference } from "../services/legalService";
import { requestAccountDeletion } from "../services/accountService";
import { endStoreScreenshotSession, isStoreScreenshotCapture } from "../screenshotSession";
import { bindClientStorageToUser, clearAuthScopedClientState } from "../services/clientStorageSecurity";

const Z = {
  bg:"var(--zuno-bg)", card:"var(--zuno-surface)", elevated:"var(--zuno-elevated)", cardBd:"var(--zuno-line)",
  cyan:"#00e5ff", purple:"#7c3aed", pink:"#ec4899",
  green:"#22c55e", red:"#ef4444",
  textPri:"var(--zuno-text)", textSec:"var(--zuno-muted)", textMut:"var(--zuno-text-subtle)",
};

function formatarTelefone(tel) {
  if (!tel) return "Não informado";
  const n = tel.replace(/\D/g,"");
  if (n.length===11) return `(${n.slice(0,2)}) ${n.slice(2,7)}-${n.slice(7)}`;
  if (n.length===10) return `(${n.slice(0,2)}) ${n.slice(2,6)}-${n.slice(6)}`;
  return tel;
}

function formatarData(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("pt-BR",{day:"2-digit",month:"short",year:"numeric"});
}

function comprimirImagem(file) {
  return new Promise((resolve, reject) => {
    const MAX = 240;
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > h) { if (w > MAX) { h = Math.round(h*MAX/w); w = MAX; } }
        else       { if (h > MAX) { w = Math.round(w*MAX/h); h = MAX; } }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.75));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

function SubTela({ titulo, icone, onVoltar, children }) {
  return (
    <div style={{ minHeight:"100vh", background:Z.bg, display:"flex", flexDirection:"column", fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{ padding:"20px 20px 16px", display:"flex", alignItems:"center", gap:14, borderBottom:`1px solid ${Z.cardBd}`, background:Z.card }}>
        <button onClick={onVoltar} style={{ width:36, height:36, borderRadius:"50%", border:`1px solid ${Z.cardBd}`, background:Z.elevated, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={Z.textSec} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        {icone && <span style={{ fontSize:20 }}>{icone}</span>}
        <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:Z.textPri }}>{titulo}</h2>
      </div>
      <div style={{ flex:1, overflowY:"auto", animation:"fadeUp 0.35s ease both" }}>{children}</div>
    </div>
  );
}

function DadoLinha({ label, valor, verificado, statusText, cor, onClick, icon }) {
  return (
    <button type="button" className="account-data-row" onClick={onClick} disabled={!onClick}>
      <span className="account-data-row-icon" aria-hidden="true">{icon}</span>
      <div style={{ flex:1, minWidth:0, textAlign:"left" }}>
        <div style={{ fontSize:11, color:Z.textMut, letterSpacing:0.8, textTransform:"uppercase", marginBottom:4 }}>{label}</div>
        <div style={{ fontSize:15, fontWeight:600, color:cor||Z.textPri, display:"flex", alignItems:"center", gap:8 }}>
          {valor}
          {verificado && (
            <span className="account-data-verified"><LuCheck />{statusText || "Verificado"}</span>
          )}
        </div>
      </div>
      {onClick && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={Z.textMut} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>}
    </button>
  );
}

// ─── MEUS DADOS ────────────────────────────────────────────────
function MeusDados({ usuario, email, emailVerificado, telefone, telefoneVerificado, foto, onVoltar, inputFotoRef, onTrocarFoto, onEnviarCodigoTelefone, onConfirmarTelefone, onAtualizarEmail, onAtualizarNome }) {
  const temTelefone = telefone && telefone !== "Não informado";

  const [editando, setEditando] = useState(null); // "telefone"|"email"|"nome"
  const [valor, setValor] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState(null);
  const [toast, setToast] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [telefonePendente, setTelefonePendente] = useState("");
  const [codigoSms, setCodigoSms] = useState("");

  function abrirEditar(campo) {
    setEditando(campo);
    setValor(campo==="telefone"?(telefone||""):campo==="email"?(email||""):(usuario||""));
    setVerificationId(""); setTelefonePendente(""); setCodigoSms("");
    setMsg(null);
  }

  async function salvar() {
    setSalvando(true); setMsg(null);
    try {
      if (editando==="telefone") {
        if (!verificationId) {
          const nums = valor.replace(/\D/g,"");
          if (nums.length < 10) throw new Error("Use DDD + número com 10 ou 11 dígitos");
          const id = await onEnviarCodigoTelefone(nums);
          setVerificationId(id); setTelefonePendente(nums); setMsg({ tipo:"ok", texto:"Código SMS enviado." });
          setSalvando(false); return;
        }
        if (codigoSms.length !== 6) throw new Error("Informe o código de 6 dígitos");
        await onConfirmarTelefone(verificationId, codigoSms, telefonePendente);
        setMsg({ tipo:"ok", texto:"✓ Alterações salvas" });
        setToast("✓ Alterações salvas");
      } else if (editando==="email") {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valor)) throw new Error("E-mail inválido");
        const result = await onAtualizarEmail(valor.trim().toLowerCase());
        setMsg({ tipo:"ok", texto:result?.verificationRequired ? "Verifique seu novo e-mail para confirmar." : "✓ Alterações salvas" });
        setToast(result?.verificationRequired ? "Link de confirmação enviado" : "✓ Alterações salvas");
      } else if (editando==="nome") {
        if (!valor.trim() || valor.trim().length < 2) throw new Error("Nome muito curto");
        await onAtualizarNome(valor.trim());
        setMsg({ tipo:"ok", texto:"✓ Alterações salvas" });
        setToast("✓ Alterações salvas");
      }
      setTimeout(() => { setEditando(null); setMsg(null); }, 2000);
      setTimeout(() => setToast(""), 3200);
    } catch(e) {
      const txt = e.message||"";
      const traduzido = txt.includes("Missing or insufficient permissions")?"Sem permissão. Saia e entre novamente.":
        txt.includes("network")||txt.includes("Network")?"Sem conexão. Verifique sua internet.":txt||"Erro ao salvar.";
      setMsg({ tipo:"erro", texto:traduzido });
    }
    setSalvando(false);
  }

  return (
    <SubTela titulo="Meus dados" icone={<LuUserRound />} onVoltar={onVoltar}>
      <input ref={inputFotoRef} type="file" accept="image/*" hidden onChange={onTrocarFoto} />
      <div className="account-data-profile">
        <div style={{ position:"relative", cursor:"pointer" }} onClick={()=>inputFotoRef.current?.click()}>
          <PassengerAvatar
            className="account-data-avatar"
            photoURL={foto}
            name={usuario}
            style={{ width:80, height:80, borderRadius:"50%", border:`3px solid rgba(0,229,255,0.4)` }}
          />
          <div style={{ position:"absolute", bottom:2, right:2, width:24, height:24, borderRadius:"50%", background:Z.card, border:`2px solid ${Z.bg}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={Z.cyan} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4z"/></svg>
          </div>
        </div>
        <strong>Foto de perfil</strong>
        <p>Toque para alterar</p>
      </div>

      <div className="account-data-card">
        <DadoLinha icon={<LuUserRound />} label="Nome completo" valor={usuario||"—"} onClick={()=>abrirEditar("nome")} />
        <DadoLinha icon={<LuMail />} label="E-mail" valor={email||<span className="account-data-action">Adicionar e-mail</span>} verificado={emailVerificado} onClick={()=>abrirEditar("email")} />
        <DadoLinha icon={<LuPhone />} label="Telefone"
          valor={temTelefone ? formatarTelefone(telefone) : <span className="account-data-action">Adicionar telefone</span>}
          verificado={telefoneVerificado} statusText="Verificado" cor={temTelefone?Z.textPri:undefined}
          onClick={()=>abrirEditar("telefone")} />
      </div>

      {!temTelefone && (
        <div style={{ margin:"12px 16px 0", padding:"12px 16px", background:"rgba(239,68,68,0.07)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:12, display:"flex", gap:10 }}>
          <LuCircleAlert aria-hidden="true" style={{ fontSize:18, flexShrink:0 }} />
          <p style={{ color:"rgba(255,100,100,0.85)", fontSize:12, lineHeight:1.6, margin:0 }}>Adicione seu telefone para receber atualizações sobre suas corridas.</p>
        </div>
      )}

      {toast && <div className="account-data-toast" role="status">{toast}</div>}

      {editando && (
        <div className="account-edit-overlay"
          onClick={()=>!salvando&&setEditando(null)}>
          <div className="account-edit-sheet" role="dialog" aria-modal="true" aria-labelledby="account-edit-title" onClick={e=>e.stopPropagation()}>
            <div className="account-edit-handle" />
            <p id="account-edit-title" className="account-edit-title">
              {editando==="nome"?"Editar nome":editando==="telefone"?(verificationId?"Confirmar telefone":"Alterar telefone"):(email?"Editar e-mail":"Adicionar e-mail")}
            </p>
            <p className="account-edit-helper">
              {editando==="nome"?"Seu nome é exibido aos motoristas":editando==="telefone"?(verificationId?`Digite o código enviado para ${formatarTelefone(telefonePendente)}`:"Para alterar seu telefone, enviaremos um código de verificação para o novo número."):"O e-mail só será marcado como verificado após a confirmação real."}
            </p>
            <input
              type={editando==="email"?"email":editando==="telefone"?"tel":"text"}
              value={editando==="telefone"&&verificationId?codigoSms:valor}
              onChange={e=>editando==="telefone"&&verificationId?setCodigoSms(e.target.value.replace(/\D/g,"").slice(0,6)):setValor(e.target.value)}
              inputMode={editando==="telefone"?"numeric":undefined}
              placeholder={editando==="nome"?"Seu nome completo":editando==="telefone"?(verificationId?"000000":"(11) 99999-0000"):"seuemail@exemplo.com"}
              autoFocus
              className="account-edit-input"
            />
            {msg && (
              <div style={{ padding:"10px 14px", borderRadius:10, marginBottom:12, background:msg.tipo==="ok"?"rgba(34,197,94,0.1)":"rgba(239,68,68,0.1)", border:`1px solid ${msg.tipo==="ok"?"rgba(34,197,94,0.3)":"rgba(239,68,68,0.3)"}` }}>
                <p style={{ fontSize:13, color:msg.tipo==="ok"?"#22c55e":"#f87171", margin:0 }}>{msg.texto}</p>
              </div>
            )}
            <button className="account-edit-save" onClick={salvar} disabled={salvando||(!valor.trim()&&!verificationId)||(verificationId&&codigoSms.length!==6)}>
              {salvando?"Salvando...":editando==="telefone"&&!verificationId?"Enviar código SMS":verificationId?"Confirmar e salvar":"Salvar alterações"}
            </button>
            <button className="account-edit-cancel" onClick={()=>setEditando(null)} disabled={salvando}>Cancelar</button>
            <div id="phone-update-recaptcha" />
          </div>
        </div>
      )}
    </SubTela>
  );
}

// ─── HISTÓRICO DE VIAGENS ─────────────────────────────────────
function HistoricoCorridas({ onVoltar }) {
  const [corridas, setCorridas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [reportando, setReportando] = useState(null);
  const [motivoReport, setMotivoReport] = useState("");
  const [descReport, setDescReport] = useState("");
  const [enviandoReport, setEnviandoReport] = useState(false);
  const [reportEnviado, setReportEnviado] = useState(false);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setCarregando(false); return; }
    getHistoricoCorridas(user.uid, "passenger", { pageSize:30 })
      .then(({ corridas:lista }) => { setCorridas(lista); setCarregando(false); })
      .catch(() => setCarregando(false));
  }, []);

  const MOTIVOS = ["Cobrou valor errado","Rota errada","Motorista grosseiro","Carro diferente do cadastro","Problema com pagamento","Outro"];

  async function enviarReport() {
    if (!motivoReport || !reportando) return;
    setEnviandoReport(true);
    try {
      const { reportarUsuario } = await import("../services/rideService");
      await reportarUsuario({ rideId:reportando.id, reportadoPor:auth.currentUser?.uid||"", reportadoTipo:"motorista", motivo:motivoReport, descricao:descReport });
      setReportEnviado(true);
      setTimeout(() => { setReportando(null); setMotivoReport(""); setDescReport(""); setReportEnviado(false); }, 2000);
    } catch(e) { console.error(e); }
    setEnviandoReport(false);
  }

  const statusLabel = (s) => s==="completed"?"Concluída":s==="cancelled"?"Cancelada":s==="in_progress"?"Em andamento":"Pendente";
  const statusCor   = (s) => s==="completed"?"#22c55e":s==="cancelled"?"#ef4444":"#f59e0b";

  return (
    <SubTela titulo="Minhas viagens" icone={<LuCarFront />} onVoltar={onVoltar}>
      {carregando ? (
        <div style={{ display:"flex", justifyContent:"center", alignItems:"center", minHeight:"40vh" }}>
          <div style={{ width:32, height:32, borderRadius:"50%", border:"3px solid rgba(168,85,247,0.2)", borderTopColor:"#a855f7", animation:"spin 0.8s linear infinite" }}/>
        </div>
      ) : corridas.length === 0 ? (
        <div style={{ textAlign:"center", padding:"60px 20px" }}>
          <LuCarFront aria-hidden="true" style={{ fontSize:48, marginBottom:16, color:Z.cyan }} />
          <p style={{ color:Z.textSec, fontSize:15, margin:"0 0 8px" }}>Nenhuma viagem ainda</p>
          <p style={{ color:Z.textMut, fontSize:13, margin:0 }}>Suas corridas aparecerão aqui.</p>
        </div>
      ) : (
        <div style={{ padding:"12px 16px 100px" }}>
          {corridas.map(c => (
            <div key={c.id} style={{ background:Z.card, border:`1px solid ${Z.cardBd}`, borderRadius:16, marginBottom:12, overflow:"hidden" }}>
              <div style={{ padding:"14px 16px" }}>
                <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
                  <div>
                    <p style={{ color:Z.textMut, fontSize:11, margin:"0 0 3px" }}>{formatarData(c.concluidoEm||c.criadoEm)}</p>
                    <p style={{ color:Z.textPri, fontWeight:700, fontSize:15, margin:0 }}>{c.motoristaNome||"Motorista"}</p>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <p style={{ color:"#22c55e", fontWeight:800, fontSize:16, margin:"0 0 3px" }}>R$ {Number(c.valor||0).toFixed(2)}</p>
                    <span style={{ background:`${statusCor(c.status)}22`, border:`1px solid ${statusCor(c.status)}55`, borderRadius:20, padding:"2px 8px", color:statusCor(c.status), fontSize:11, fontWeight:700 }}>
                      {statusLabel(c.status)}
                    </span>
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, paddingTop:3, flexShrink:0 }}>
                    <div style={{ width:7, height:7, borderRadius:"50%", background:"#22c55e" }}/>
                    <div style={{ width:1, height:16, background:"rgba(255,255,255,0.1)" }}/>
                    <div style={{ width:7, height:7, borderRadius:2, background:"#ef4444" }}/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ color:Z.textSec, fontSize:12, margin:"0 0 14px", lineHeight:1.4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {c.origem?.endereco||"Origem"}
                    </p>
                    <p style={{ color:Z.textSec, fontSize:12, margin:0, lineHeight:1.4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {c.destino?.nome||c.destino?.endereco||"Destino"}
                    </p>
                  </div>
                </div>
                {c.status==="completed" && (
                  <button onClick={()=>{ setReportando(c); setMotivoReport(""); setDescReport(""); setReportEnviado(false); }}
                    style={{ marginTop:12, width:"100%", padding:"9px", borderRadius:10, border:"1px solid rgba(239,68,68,0.2)", background:"transparent", color:"rgba(239,68,68,0.6)", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                    Reportar problema
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal reportar */}
      {reportando && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", backdropFilter:"blur(6px)", zIndex:9999, display:"flex", alignItems:"flex-end" }}
          onClick={()=>!enviandoReport&&setReportando(null)}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%", background:"#0d1527", borderRadius:"24px 24px 0 0", padding:"24px 20px 48px", border:"1px solid rgba(255,255,255,0.08)", borderBottom:"none", maxHeight:"80vh", overflowY:"auto" }}>
            <div style={{ width:36, height:4, background:"rgba(255,255,255,0.12)", borderRadius:10, margin:"0 auto 20px" }}/>
            {reportEnviado ? (
              <div style={{ textAlign:"center", padding:"20px 0" }}>
                <LuCheck aria-hidden="true" style={{ fontSize:48, marginBottom:12, color:Z.green }} />
                <p style={{ color:"#22c55e", fontWeight:700, fontSize:16, margin:0 }}>Problema reportado!</p>
                <p style={{ color:Z.textSec, fontSize:13, margin:"8px 0 0" }}>Nossa equipe analisará em até 48h.</p>
              </div>
            ) : (
              <>
                <p style={{ color:"#fff", fontWeight:800, fontSize:16, margin:"0 0 4px" }}>Reportar problema</p>
                <p style={{ color:Z.textSec, fontSize:12, margin:"0 0 16px" }}>Corrida com {reportando.motoristaNome||"motorista"}</p>
                <p style={{ color:Z.textMut, fontSize:11, fontWeight:700, letterSpacing:0.8, textTransform:"uppercase", margin:"0 0 10px" }}>Selecione o motivo</p>
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
                  {MOTIVOS.map(m => (
                    <button key={m} onClick={()=>setMotivoReport(m)}
                      style={{ padding:"11px 14px", borderRadius:12, border:`1px solid ${motivoReport===m?"rgba(168,85,247,0.5)":Z.cardBd}`, background:motivoReport===m?"rgba(168,85,247,0.1)":"rgba(255,255,255,0.03)", color:motivoReport===m?"#a855f7":Z.textSec, fontSize:13, fontWeight:motivoReport===m?700:500, cursor:"pointer", textAlign:"left" }}>
                      {m}
                    </button>
                  ))}
                </div>
                <textarea value={descReport} onChange={e=>setDescReport(e.target.value)} placeholder="Descreva o que aconteceu (opcional)..." rows={3}
                  style={{ width:"100%", padding:"12px 14px", borderRadius:12, border:`1px solid ${Z.cardBd}`, background:"rgba(255,255,255,0.04)", color:"#fff", fontSize:13, fontFamily:"inherit", outline:"none", resize:"none", boxSizing:"border-box", marginBottom:14 }}/>
                <button onClick={enviarReport} disabled={!motivoReport||enviandoReport}
                  style={{ width:"100%", padding:"14px", borderRadius:14, border:"none", background:!motivoReport||enviandoReport?"rgba(255,255,255,0.08)":"linear-gradient(135deg,#7c3aed,#a855f7)", color:!motivoReport||enviandoReport?"rgba(255,255,255,0.25)":"#fff", fontSize:15, fontWeight:700, cursor:!motivoReport||enviandoReport?"not-allowed":"pointer", fontFamily:"inherit" }}>
                  {enviandoReport?"Enviando...":"Enviar report"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </SubTela>
  );
}

// ─── LOCAIS SALVOS ────────────────────────────────────────────
function LocaisSalvos({ onVoltar }) {
  const [casaSalva, setCasaSalva] = useState(null);
  const [trabalhoSalvo, setTrabalhoSalvo] = useState(null);
  const [editando, setEditando] = useState(null); // "casa"|"trabalho"
  const [input, setInput] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const c = localStorage.getItem("casa");
    const t = localStorage.getItem("trabalho");
    if (c) try { setCasaSalva(JSON.parse(c)); } catch{}
    if (t) try { setTrabalhoSalvo(JSON.parse(t)); } catch{}
  }, []);

  function salvar() {
    if (!input.trim()) return;
    const dados = { lat:null, lng:null, endereco:input.trim() };
    localStorage.setItem(editando, JSON.stringify(dados));
    if (editando==="casa") setCasaSalva(dados);
    else setTrabalhoSalvo(dados);
    setMsg(`${editando==="casa"?"Casa":"Trabalho"} salvo`);
    setTimeout(() => { setEditando(null); setMsg(""); }, 1500);
  }

  function remover(tipo) {
    localStorage.removeItem(tipo);
    if (tipo==="casa") setCasaSalva(null);
    else setTrabalhoSalvo(null);
  }

  const locais = [
    { key:"casa", icon:<LuHouse />, label:"Casa", salvo:casaSalva },
    { key:"trabalho", icon:<LuBriefcaseBusiness />, label:"Trabalho", salvo:trabalhoSalvo },
  ];

  return (
    <SubTela titulo="Locais salvos" icone={<LuMapPin />} onVoltar={onVoltar}>
      <div style={{ padding:"16px 16px 100px" }}>
        <p style={{ color:Z.textSec, fontSize:13, lineHeight:1.6, margin:"0 0 16px" }}>
          Salve seus destinos frequentes para acessá-los com um toque na tela inicial.
        </p>
        {locais.map(({ key, icon, label, salvo }) => (
          <div key={key} style={{ background:Z.card, border:`1px solid ${salvo?"rgba(124,58,237,0.35)":Z.cardBd}`, borderRadius:16, marginBottom:12, overflow:"hidden" }}>
            <div style={{ padding:"16px", display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:12, background:salvo?"rgba(124,58,237,0.15)":"rgba(255,255,255,0.04)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>{icon}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ color:Z.textPri, fontWeight:700, fontSize:14, margin:"0 0 3px" }}>{label}</p>
                <p style={{ color:salvo?Z.textSec:Z.textMut, fontSize:12, margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {salvo ? salvo.endereco : "Não definido"}
                </p>
              </div>
              <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                <button onClick={()=>{ setEditando(key); setInput(salvo?.endereco||""); setMsg(""); }}
                  style={{ padding:"7px 14px", borderRadius:10, border:"1px solid rgba(124,58,237,0.35)", background:"rgba(124,58,237,0.1)", color:"#a855f7", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                  {salvo?"Editar":"Definir"}
                </button>
                {salvo && (
                  <button onClick={()=>remover(key)}
                    style={{ width:32, height:32, borderRadius:10, border:"1px solid rgba(239,68,68,0.2)", background:"transparent", color:"rgba(239,68,68,0.6)", fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <LuX />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {editando && (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", backdropFilter:"blur(6px)", zIndex:9999, display:"flex", alignItems:"flex-end" }}
            onClick={()=>setEditando(null)}>
            <div onClick={e=>e.stopPropagation()} style={{ width:"100%", background:"#0d1527", borderRadius:"24px 24px 0 0", padding:"24px 20px 48px", border:"1px solid rgba(255,255,255,0.08)", borderBottom:"none" }}>
              <div style={{ width:36, height:4, background:"rgba(255,255,255,0.12)", borderRadius:10, margin:"0 auto 20px" }}/>
              <p style={{ fontSize:16, fontWeight:700, color:"#fff", margin:"0 0 6px" }}>
                {editando==="casa"?"Endereço de casa":"Endereço do trabalho"}
              </p>
              <p style={{ fontSize:12, color:Z.textSec, margin:"0 0 16px" }}>Digite o endereço completo</p>
              {msg && <div style={{ padding:"10px 14px", borderRadius:10, marginBottom:12, background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.3)" }}><p style={{ color:"#22c55e", fontSize:13, margin:0 }}>{msg}</p></div>}
              <input type="text" value={input} onChange={e=>setInput(e.target.value)} autoFocus
                placeholder={editando==="casa"?"Ex: Rua das Flores, 123 — Jaraguá do Sul":"Ex: Av. Getúlio Vargas, 100 — Centro"}
                style={{ width:"100%", padding:"14px 16px", borderRadius:14, border:"1px solid rgba(255,255,255,0.15)", background:"rgba(255,255,255,0.06)", color:"#fff", fontSize:15, fontFamily:"inherit", outline:"none", boxSizing:"border-box", marginBottom:12 }}
              />
              <button onClick={salvar} disabled={!input.trim()}
                style={{ width:"100%", padding:"15px", borderRadius:14, border:"none", background:!input.trim()?"rgba(255,255,255,0.08)":"linear-gradient(135deg,#7c3aed,#a855f7)", color:!input.trim()?"rgba(255,255,255,0.25)":"#fff", fontSize:15, fontWeight:700, cursor:!input.trim()?"not-allowed":"pointer", fontFamily:"inherit" }}>
                Salvar local
              </button>
            </div>
          </div>
        )}
      </div>
    </SubTela>
  );
}

// ─── CUPONS E DESCONTOS ───────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function Cupons({ onVoltar }) {
  const [desconto, setDesconto] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setCarregando(false); return; }
    getDescontoDisponivel(user.uid)
      .then(d => { setDesconto(d); setCarregando(false); })
      .catch(() => setCarregando(false));
  }, []);

  return (
    <SubTela titulo="Cupons e descontos" icone={<LuTicketPercent />} onVoltar={onVoltar}>
      <div style={{ padding:"16px 16px 100px" }}>
        {carregando ? (
          <div style={{ display:"flex", justifyContent:"center", padding:"40px 0" }}>
            <div style={{ width:28, height:28, borderRadius:"50%", border:"3px solid rgba(168,85,247,0.2)", borderTopColor:"#a855f7", animation:"spin 0.8s linear infinite" }}/>
          </div>
        ) : (
          <>
            {/* Desconto atual */}
            {desconto?.disponivel ? (
              <div style={{ background:"linear-gradient(135deg,rgba(124,58,237,0.2),rgba(168,85,247,0.1))", border:"1px solid rgba(168,85,247,0.4)", borderRadius:20, padding:"20px", marginBottom:16, textAlign:"center" }}>
                <LuTicketPercent aria-hidden="true" style={{ fontSize:36, marginBottom:8, color:Z.purple }} />
                <p style={{ color:"#a855f7", fontWeight:800, fontSize:18, margin:"0 0 4px" }}>Desconto disponível!</p>
                <p style={{ color:"#22c55e", fontWeight:900, fontSize:32, margin:"0 0 8px" }}>−R$ {desconto.valorDesconto?.toFixed(2)}</p>
                <p style={{ color:Z.textSec, fontSize:13, margin:0 }}>{desconto.mensagem}</p>
              </div>
            ) : (
              <div style={{ background:Z.card, border:`1px solid ${Z.cardBd}`, borderRadius:16, padding:"20px", marginBottom:16, textAlign:"center" }}>
                <LuCheck aria-hidden="true" style={{ fontSize:36, marginBottom:8, color:Z.green }} />
                <p style={{ color:Z.textSec, fontSize:15, margin:"0 0 4px" }}>Sem descontos hoje</p>
                <p style={{ color:Z.textMut, fontSize:12, margin:0 }}>Limite de descontos diários atingido. Volta amanhã!</p>
              </div>
            )}

            {/* Como funciona */}
            <div style={{ background:Z.card, border:`1px solid ${Z.cardBd}`, borderRadius:16, padding:"16px", marginBottom:12 }}>
              <p style={{ color:Z.textPri, fontWeight:700, fontSize:14, margin:"0 0 14px", display:"flex",gap:8,alignItems:"center" }}><LuTags /> Como funciona</p>
              {[
                { n:"1ª corrida", desc:"R$8,00 de desconto de boas-vindas", cor:"#22c55e" },
                { n:"2ª a 5ª", desc:"R$4,00 por corrida", cor:"#a855f7" },
                { n:"6ª em diante", desc:"R$2,00 por corrida", cor:"#06b6d4" },
              ].map((item,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:12, marginBottom: i<2?12:0 }}>
                  <div style={{ width:36, height:36, borderRadius:10, background:`${item.cor}22`, border:`1px solid ${item.cor}44`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <p style={{ color:item.cor, fontWeight:800, fontSize:11, margin:0, textAlign:"center", lineHeight:1.2 }}>{item.n}</p>
                  </div>
                  <p style={{ color:Z.textSec, fontSize:13, margin:0 }}>{item.desc}</p>
                </div>
              ))}
              <p style={{ color:Z.textMut, fontSize:11, margin:"12px 0 0", lineHeight:1.5 }}>
                * Máx. 2 descontos por dia. Corridas acima de R$12 e 2km. Desconto aplicado automaticamente na hora de pedir.
              </p>
            </div>
          </>
        )}
      </div>
    </SubTela>
  );
}

// ─── MOTORISTAS FAVORITOS ─────────────────────────────────────
function MotoristasF({ onVoltar }) {
  const [favoritos, setFavoritos] = useState([]);

  useEffect(() => {
    try {
      const f = JSON.parse(localStorage.getItem("motoristasFavoritos")||"[]");
      setFavoritos(f);
    } catch {}
  }, []);

  function remover(id) {
    const novo = favoritos.filter(f => f.id !== id);
    setFavoritos(novo);
    localStorage.setItem("motoristasFavoritos", JSON.stringify(novo));
  }

  return (
    <SubTela titulo="Motoristas favoritos" icone={<LuStar />} onVoltar={onVoltar}>
      {favoritos.length === 0 ? (
        <div style={{ padding:"60px 20px", textAlign:"center" }}>
          <div style={{ width:80, height:80, borderRadius:"50%", background:"rgba(255,255,255,0.04)", border:`1px solid ${Z.cardBd}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:36, margin:"0 auto 16px" }}>⭐</div>
          <p style={{ color:Z.textSec, fontSize:15, margin:"0 0 8px" }}>Nenhum favorito ainda</p>
          <p style={{ color:Z.textMut, fontSize:13, margin:0, lineHeight:1.6 }}>Toque na estrela ⭐ durante uma corrida para favoritar o motorista.</p>
        </div>
      ) : (
        <div style={{ padding:"16px 16px 100px" }}>
          {favoritos.map(f => (
            <div key={f.id} style={{ background:Z.card, border:`1px solid ${Z.cardBd}`, borderRadius:16, marginBottom:12, padding:"14px 16px", display:"flex", alignItems:"center", gap:12 }}>
              {f.foto ? (
                <img src={f.foto} alt="" style={{ width:48, height:48, borderRadius:"50%", objectFit:"cover", border:"2px solid rgba(245,158,11,0.4)", flexShrink:0 }}/>
              ) : (
                <div style={{ width:48, height:48, borderRadius:"50%", background:"linear-gradient(135deg,#f59e0b,#d97706)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:700, color:"#fff", flexShrink:0 }}>
                  {(f.nome||"M").charAt(0).toUpperCase()}
                </div>
              )}
              <div style={{ flex:1 }}>
                <p style={{ color:Z.textPri, fontWeight:700, fontSize:14, margin:"0 0 2px" }}>{f.nome}</p>
                {f.nota && <p style={{ color:"#f59e0b", fontSize:12, margin:0 }}>⭐ {Number(f.nota).toFixed(1)}</p>}
              </div>
              <button aria-label="Remover favorito" onClick={()=>remover(f.id)} style={{ width:32, height:32, borderRadius:10, border:"1px solid rgba(239,68,68,0.2)", background:"transparent", color:"rgba(239,68,68,0.6)", fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><LuX /></button>
            </div>
          ))}
        </div>
      )}
    </SubTela>
  );
}

// ─── ACESSIBILIDADE ───────────────────────────────────────────
function Acessibilidade({ onVoltar }) {
  const { preferences: prefs, togglePreference } = useAccessibility();

  const opcoes = [
    { key:"textoGrande",       icon:<LuType />, label:"Texto grande",          desc:"Aumenta o tamanho do texto em todo o app" },
    { key:"altoContraste",     icon:<LuContrast />, label:"Alto contraste",         desc:"Aumenta o contraste das cores para melhor leitura" },
    { key:"reduzirAnimacoes",  icon:<LuZap />, label:"Reduzir animações",      desc:"Desativa animações para evitar desconforto visual" },
  ];

  return (
    <SubTela titulo="Acessibilidade" icone={<LuAccessibility />} onVoltar={onVoltar}>
      <div style={{ padding:"20px 16px 100px" }}>
        <p style={{ color:Z.textMut, fontSize:12, lineHeight:1.6, margin:"0 0 20px" }}>
          Configure o app de acordo com suas necessidades.
        </p>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {opcoes.map(({ key, icon, label, desc }) => (
            <div key={key} className="accessibility-option"
              style={{ background:Z.card, border:`1px solid ${prefs[key] ? "rgba(0,229,255,0.3)" : Z.cardBd}`, borderRadius:16, padding:"16px", display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:12, background:"rgba(255,255,255,0.05)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>{icon}</div>
              <div style={{ flex:1 }}>
                <p style={{ color:Z.textPri, fontWeight:700, fontSize:14, margin:"0 0 3px" }}>{label}</p>
                <p style={{ color:Z.textMut, fontSize:12, margin:0, lineHeight:1.4 }}>{desc}</p>
              </div>
              {/* Toggle switch */}
              <button type="button" className="zuno-accessibility-switch" role="switch" aria-checked={prefs[key]} aria-label={`${label}: ${prefs[key] ? "ativado" : "desativado"}`} onClick={() => togglePreference(key)}>
                <span />
              </button>
            </div>
          ))}
        </div>
        <div style={{ marginTop:24, padding:"14px", background:"rgba(255,255,255,0.03)", borderRadius:14, border:`1px solid ${Z.cardBd}` }}>
          <p style={{ color:Z.textMut, fontSize:12, margin:0, lineHeight:1.6 }}>
            Precisa de ajuda adicional? Entre em contato pelo <span style={{ color:Z.cyan }}>Fale conosco</span>.
          </p>
        </div>
      </div>
    </SubTela>
  );
}

// ─── FALE CONOSCO ─────────────────────────────────────────────
function FaleConosco({ onVoltar }) {
  const canais = [
    { icon:<LuPhone />, label:"Telefone", valor:"(11) 91793-4340", href:"tel:+5511917934340", cor:Z.green },
    { icon:<LuMail />, label:"E-mail", valor:"suporte@zuno.app", href:"mailto:suporte@zuno.app", cor:Z.cyan },
  ];
  return (
    <SubTela titulo="Fale conosco" icone={<LuMessageCircle />} onVoltar={onVoltar}>
      <div style={{ padding:"20px 16px 0" }}>
        <p style={{ color:Z.textSec, fontSize:13, lineHeight:1.6, margin:"0 0 16px" }}>Nossa equipe está disponível para te ajudar. Escolha o canal de preferência:</p>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {canais.map((c,i) => (
            <a key={i} href={c.href} target="_blank" rel="noreferrer" style={{ textDecoration:"none" }}>
              <div style={{ display:"flex", alignItems:"center", gap:14, padding:"16px", background:Z.card, borderRadius:14, border:`1px solid ${Z.cardBd}` }}>
                <div style={{ width:44, height:44, borderRadius:12, background:`rgba(255,255,255,0.04)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>{c.icon}</div>
                <div style={{ flex:1 }}>
                  <p style={{ color:Z.textMut, fontSize:11, letterSpacing:0.8, textTransform:"uppercase", margin:"0 0 3px" }}>{c.label}</p>
                  <p style={{ color:c.cor, fontSize:15, fontWeight:700, margin:0 }}>{c.valor}</p>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={Z.textMut} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </div>
            </a>
          ))}
        </div>
      </div>
    </SubTela>
  );
}

/* ─── TELA PRINCIPAL ─────────────────────────────────────────── */
function PrivacyCenter({ onVoltar, onDelete }) {
  const [marketing, setMarketing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getLegalReadiness("passenger").then((data) => setMarketing(data.marketing === true)).catch(() => {}); }, []);
  const toggleMarketing = async (enabled) => {
    setSaving(true);
    try { await updateMarketingPreference(enabled); setMarketing(enabled); }
    finally { setSaving(false); }
  };
  const location = localStorage.getItem("locationPermission") || "não solicitado";
  const notifications = localStorage.getItem("zunoNotificationPrimer") || "não solicitado";
  const linkStyle = { display:"block", padding:"14px 0", color:"#8ec7ff", borderBottom:"1px solid rgba(255,255,255,.08)", textDecoration:"none" };
  return <div className="zuno-app-shell" style={{ minHeight:"100vh", padding:"24px 18px 80px", color:"#f5f9ff" }}>
    <button onClick={onVoltar} style={{ background:"transparent", border:0, color:"#d9e8fa", minHeight:44 }}>← Voltar</button>
    <h1>Privacidade e documentos</h1><p style={{ color:"#9fb2ca", lineHeight:1.55 }}>Controle preferências opcionais e consulte os documentos vigentes.</p>
    <section style={{ padding:18, borderRadius:18, background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.08)" }}>
      <a href="/termos" style={linkStyle}>Termos de Uso — Passageiro</a><a href="/privacidade" style={linkStyle}>Aviso de Privacidade</a><a href="/pagamentos" style={linkStyle}>Política de Pagamentos</a><a href="/cancelamento-reembolso" style={linkStyle}>Cancelamento e Reembolso</a><a href="/diretrizes-comunidade" style={linkStyle}>Diretrizes da Comunidade</a><a href="/cookies" style={linkStyle}>Cookies e armazenamento web</a>
    </section>
    <section style={{ marginTop:18, padding:18, borderRadius:18, background:"rgba(255,255,255,.04)" }}><h2 style={{ fontSize:18 }}>Permissões</h2><p>Localização: <b>{location}</b></p><p>Notificações: <b>{notifications}</b></p><small style={{ color:"#9fb2ca" }}>Altere permissões nativas nas configurações do dispositivo. A Zuno não solicita localização em segundo plano ao passageiro.</small></section>
    <label style={{ marginTop:18, padding:18, borderRadius:18, background:"rgba(255,255,255,.04)", display:"flex", gap:12 }}><input type="checkbox" checked={marketing} disabled={saving} onChange={(e) => toggleMarketing(e.target.checked)} /><span><b>Novidades e promoções</b><br/><small style={{ color:"#9fb2ca" }}>Opcional. Você pode retirar quando quiser.</small></span></label>
    <section style={{ marginTop:18, padding:18, borderRadius:18, background:"rgba(255,255,255,.04)" }}><h2 style={{ fontSize:18 }}>Seus dados</h2><p style={{ color:"#9fb2ca" }}>Solicite acesso ou portabilidade pelo contato de privacidade: privacidade@zunomovel.com.br.</p><button onClick={onDelete} style={{ minHeight:46, borderRadius:12, border:"1px solid #ef4444", background:"transparent", color:"#ff7777", padding:"0 16px" }}>Excluir minha conta</button></section>
  </div>;
}

export default function Conta() {
  const navigate = useNavigate();
  const [usuario, setUsuario]     = useState("");
  const [email, setEmail]         = useState("");
  const [telefone, setTelefone]   = useState("");
  const [foto, setFoto]           = useState("");
  const [subtela, setSubtela] = useState(() => {
    const section = new URLSearchParams(window.location.search).get("section");
    return ["dados", "historico", "locais", "motoristas", "fale", "acessibilidade"].includes(section) ? section : null;
  });
  const [carregando, setCarregando] = useState(true);
  const [salvandoFoto, setSalvandoFoto] = useState(false);
  const [toastFoto, setToastFoto] = useState(null); // { tipo:"ok"|"erro", texto }
  const inputFotoRef = useRef(null);
  const phoneVerifierRef = useRef(null);

  useEffect(() => () => phoneVerifierRef.current?.clear?.(), []);

  useEffect(() => {
    if (subtela) { window.history.pushState({ subtela }, ""); }
    else if (window.location.search) { window.history.replaceState({}, "", window.location.pathname); }
    const handlePop = () => { if (subtela) setSubtela(null); };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [subtela]);

  useEffect(() => {
    const buscarDados = async (user) => {
      if (!user) { setCarregando(false); return; }
      bindClientStorageToUser(user.uid);
      const requestedUid = user.uid;
      setUsuario("");
      setEmail("");
      setTelefone("");
      setFoto("");
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (auth.currentUser?.uid !== requestedUid) return;
        if (snap.exists()) {
          const data = snap.data();
          if (data.nome)     { setUsuario(data.nome); localStorage.setItem("usuarioLogado", data.nome); }
          if (data.telefone) { setTelefone(data.telefone); localStorage.setItem("telefone", data.telefone); }
          const emailAtual = data.emailContato || user.email || "";
          if (emailAtual)    { setEmail(emailAtual); localStorage.setItem("userEmail", emailAtual); }
          if (data.photoURL) { setFoto(data.photoURL); setCachedProfilePhoto(data.photoURL); }
          else {
            const fotoLocal = getCachedProfilePhoto();
            if (!fotoLocal && user.photoURL) { setFoto(user.photoURL); setCachedProfilePhoto(user.photoURL); }
          }
        } else {
          setUsuario(user.displayName || "");
          if (user.email) { setEmail(user.email); localStorage.setItem("userEmail", user.email); }
          if (user.photoURL) { setFoto(user.photoURL); setCachedProfilePhoto(user.photoURL); }
        }
      } catch(e) { console.error("Erro ao buscar dados:", e); }
      if (auth.currentUser?.uid === requestedUid) setCarregando(false);
    };
    const unsub = auth.onAuthStateChanged(u => { if (u) buscarDados(u); else setCarregando(false); });
    return () => unsub();
  }, []);

  async function trocarFoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setToastFoto({ tipo:"erro", texto:"Selecione uma imagem (JPG ou PNG)" });
      setTimeout(() => setToastFoto(null), 3000);
      return;
    }
    setSalvandoFoto(true);
    try {
      const dataUrl = await comprimirImagem(file);
      setFoto(dataUrl);
      setCachedProfilePhoto(dataUrl);
      const user = auth.currentUser;
      if (user) {
        await updateDoc(doc(db, "users", user.uid), { photoURL: dataUrl });
        await updateProfile(user, { photoURL: dataUrl }).catch(() => {});
      }
      setToastFoto({ tipo:"ok", texto:"Foto atualizada" });
      setTimeout(() => setToastFoto(null), 2500);
    } catch(err) {
      console.error("Erro ao salvar foto:", err);
      setToastFoto({ tipo:"erro", texto:"Erro ao salvar foto. Tente novamente." });
      setTimeout(() => setToastFoto(null), 3500);
    }
    setSalvandoFoto(false);
    if (inputFotoRef.current) inputFotoRef.current.value = "";
  }

  async function atualizarTelefone(novoTelefone) {
    const user = auth.currentUser;
    if (!user) throw new Error("Não autenticado");
    await updateDoc(doc(db, "users", user.uid), { telefone: novoTelefone });
    setTelefone(novoTelefone);
    localStorage.setItem("telefone", novoTelefone);
  }

  async function enviarCodigoTelefone(novoTelefone) {
    const user = auth.currentUser;
    if (!user) throw new Error("Não autenticado");
    phoneVerifierRef.current?.clear?.();
    const verifier = new RecaptchaVerifier(auth, "phone-update-recaptcha", { size:"invisible" });
    phoneVerifierRef.current = verifier;
    const provider = new PhoneAuthProvider(auth);
    return provider.verifyPhoneNumber(`+55${novoTelefone}`, verifier);
  }

  async function confirmarTelefone(verificationId, codigo, novoTelefone) {
    const user = auth.currentUser;
    if (!user) throw new Error("Não autenticado");
    const credential = PhoneAuthProvider.credential(verificationId, codigo);
    await updatePhoneNumber(user, credential);
    await atualizarTelefone(novoTelefone);
    phoneVerifierRef.current?.clear?.();
    phoneVerifierRef.current = null;
  }

  async function atualizarEmail(novoEmail) {
    const { verifyBeforeUpdateEmail } = await import("firebase/auth");
    const user = auth.currentUser;
    if (!user) throw new Error("Não autenticado");
    const isGoogle = user.providerData?.some(p => p.providerId==="google.com");
    if (isGoogle) {
      await updateDoc(doc(db, "users", user.uid), { emailContato: novoEmail });
      setEmail(novoEmail); localStorage.setItem("userEmail", novoEmail);
      return { verificationRequired:false };
    } else {
      await verifyBeforeUpdateEmail(user, novoEmail);
      return { verificationRequired:true };
    }
  }

  async function atualizarNome(novoNome) {
    const user = auth.currentUser;
    if (!user) throw new Error("Não autenticado");
    await updateDoc(doc(db, "users", user.uid), { nome: novoNome });
    await updateProfile(user, { displayName: novoNome }).catch(() => {});
    setUsuario(novoNome); localStorage.setItem("usuarioLogado", novoNome);
  }

  async function sair() {
    if (!isStoreScreenshotCapture) {
      await limparTokenNotificacoes("passenger");
      try { await signOut(auth); } catch(e) { console.error(e); }
    }
    clearAuthScopedClientState();
    endStoreScreenshotSession();
    navigate("/login", { replace:true });
  }

  async function excluirConta() {
    if (!window.confirm("Solicitar a exclusão da sua conta Zuno? Você será desconectado e não poderá desfazer pelo aplicativo.")) return;
    try {
      await requestAccountDeletion();
      await signOut(auth).catch(() => {});
      clearAuthScopedClientState();
      navigate("/login", { replace:true });
    } catch (error) {
      window.alert(error?.message || "Não foi possível solicitar a exclusão agora.");
    }
  }

  const temTelefone = !!(telefone && telefone.replace(/\D/g,"").length>=10);
  const telefoneAuth = (auth.currentUser?.phoneNumber || "").replace(/\D/g,"");
  const telefoneVerificado = temTelefone && !!telefoneAuth && telefoneAuth.endsWith(telefone.replace(/\D/g,""));
  const temFoto = !!foto;
  const perfilVerificado = temTelefone && temFoto;
  const perfilIncompleto = !temTelefone || !temFoto;

  if (subtela==="dados")       return <MeusDados usuario={usuario} email={email} emailVerificado={!!email && auth.currentUser?.email === email && auth.currentUser?.emailVerified === true} telefone={telefone} telefoneVerificado={telefoneVerificado} foto={foto} inputFotoRef={inputFotoRef} onTrocarFoto={trocarFoto} onVoltar={()=>setSubtela(null)} onEnviarCodigoTelefone={enviarCodigoTelefone} onConfirmarTelefone={confirmarTelefone} onAtualizarEmail={atualizarEmail} onAtualizarNome={atualizarNome} />;
  if (subtela==="historico")   return <HistoricoCorridas onVoltar={()=>setSubtela(null)} />;
  if (subtela==="locais")      return <LocaisSalvos onVoltar={()=>setSubtela(null)} />;
  if (subtela==="motoristas")    return <MotoristasF onVoltar={()=>setSubtela(null)} />;
  if (subtela==="fale")          return <FaleConosco onVoltar={()=>setSubtela(null)} />;
  if (subtela==="acessibilidade") return <Acessibilidade onVoltar={()=>setSubtela(null)} />;
  if (subtela==="privacidade") return <PrivacyCenter onVoltar={()=>setSubtela(null)} onDelete={excluirConta} />;

  const itens = [
    { section:"Conta e viagens", key:"dados", icon:<LuUserRound />, label:"Meus dados", badge:perfilIncompleto?"!":null, badgeCor:"#ef4444" },
    { section:"Conta e viagens", key:"historico", icon:<LuRoute />, label:"Minhas viagens", badge:null },
    { section:"Conta e viagens", key:"locais", icon:<LuMapPin />, label:"Locais salvos", badge:null },
    { section:"Benefícios", key:"motoristas", icon:<LuStar />, label:"Motoristas favoritos", badge:null },
    { section:"Preferências e suporte", key:"acessibilidade", icon:<LuAccessibility />, label:"Acessibilidade", badge:null },
    { section:"Preferências e suporte", key:"privacidade", icon:<LuShieldCheck />, label:"Privacidade e documentos", badge:null },
    { section:"Preferências e suporte", key:"fale", icon:<LuMessageCircle />, label:"Fale conosco", badge:null },
  ];
  const secoes = [...new Set(itens.map(item => item.section))];

  return (
    <div className="zuno-app-shell account-screen" style={{ minHeight:"100vh", display:"flex", flexDirection:"column" }}>
      <style>{`
        @keyframes fadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
        @keyframes toastIn { from{opacity:0;transform:translateY(-12px)} to{opacity:1;transform:translateY(0)} }
        * { box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
        .menu-item:active { background:rgba(255,255,255,0.05) !important; }
      `}</style>

      <input ref={inputFotoRef} type="file" accept="image/*" style={{ display:"none" }} onChange={trocarFoto} />

      <header className="zuno-page-header">
        <img src={logoZuno} alt="" aria-hidden="true" />
        <h1>Minha conta</h1>
      </header>

      {/* Toast foto */}
      {toastFoto && (
        <div style={{ position:"fixed", top:16, left:"50%", transform:"translateX(-50%)", zIndex:99999, background:toastFoto.tipo==="ok"?"rgba(34,197,94,0.95)":"rgba(239,68,68,0.95)", backdropFilter:"blur(8px)", borderRadius:50, padding:"10px 20px", animation:"toastIn 0.3s ease", whiteSpace:"nowrap", boxShadow:"0 4px 16px rgba(0,0,0,0.3)" }}>
          <span style={{ fontSize:13, color:"#fff", fontWeight:700 }}>{toastFoto.texto}</span>
        </div>
      )}

      <div className="zuno-account-profile" style={{ padding:"44px 20px 24px", position:"relative", overflow:"hidden" }}>
        <div className="account-compact-profile">
          <button className="account-profile-edit" type="button" aria-label="Editar perfil" onClick={()=>inputFotoRef.current.click()}>
            {salvandoFoto ? <span className="account-edit-spinner" /> : <LuPencil />}
          </button>

          <div className="account-profile-main">
            <PassengerAvatar className="account-reference-avatar" photoURL={foto} name={usuario} />
            <div className="account-profile-copy">
              {carregando ? <span className="account-name-skeleton" /> : <h2>{usuario || "Usuário"}</h2>}
              <p>Passageiro</p>
              {perfilVerificado && <small><LuCheck /> Conta verificada</small>}
            </div>
          </div>

          {!carregando && (
            <div className="account-contact-grid">
              <div className="account-contact-card">
                {temTelefone ? <LuPhone aria-hidden="true" /> : <LuCircleAlert aria-hidden="true" />}
                <div className="account-contact-copy">
                  <span>Telefone</span>
                  <strong>{temTelefone ? formatarTelefone(telefone) : "Não informado"}</strong>
                </div>
                {temTelefone && <span className="account-contact-check"><LuCheck /></span>}
              </div>
              <div className="account-contact-card account-contact-card--email">
                <LuMail aria-hidden="true" />
                <div className="account-contact-copy">
                  <span>E-mail</span>
                  <strong title={email}>{email || "—"}</strong>
                </div>
                {email && <span className="account-contact-check"><LuCheck /></span>}
              </div>
            </div>
          )}
        </div>

      </div>

      <div className="account-scroll-content" style={{ flex:1, overflowY:"auto", paddingBottom:68 }}>
        <IOSThemePreference />
        {secoes.map(section => {
          const sectionItems = itens.filter(item => item.section === section);
          return (
            <section className="account-menu-section" key={section}>
              <h2>{section}</h2>
              <div className="account-menu-card">
                {sectionItems.map((item, i) => (
                  <div key={item.key} className="menu-item" onClick={()=>setSubtela(item.key)}
                    style={{ display:"flex", alignItems:"center", gap:14, padding:"15px 18px", borderBottom:i<sectionItems.length-1?`1px solid ${Z.cardBd}`:"none", cursor:"pointer", transition:"background 0.15s" }}>
                    <div className="account-menu-icon" style={{ width:46, height:46, borderRadius:14, background:"rgba(52,152,255,.08)", border:"1px solid rgba(118,87,255,.18)", color:"#74a8ff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:21, flexShrink:0 }}>
                      {item.icon}
                    </div>
                    <span style={{ flex:1, fontSize:16, fontWeight:650, color:Z.textPri }}>{item.label}</span>
                    {item.badge && (
                      <div style={{ width:20, height:20, borderRadius:"50%", background:item.badgeCor, display:"flex", alignItems:"center", justifyContent:"center", marginRight:6 }}>
                        <span style={{ color:"#fff", fontSize:12, fontWeight:800 }}>{item.badge}</span>
                      </div>
                    )}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={Z.textMut} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        <div className="account-logout-card" style={{ margin:"12px 16px 0", borderRadius:16, background:"rgba(239,68,68,0.05)", border:"1px solid rgba(239,68,68,0.15)", overflow:"hidden", animation:"fadeUp 0.4s ease 0.2s both" }}>
          <div className="menu-item" onClick={sair} style={{ padding:"15px 18px", cursor:"pointer", display:"flex", alignItems:"center", gap:14, transition:"background 0.15s" }}>
            <div style={{ width:40, height:40, borderRadius:12, background:"rgba(239,68,68,0.1)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </div>
            <span style={{ flex:1, fontSize:15, fontWeight:700, color:"#ef4444" }}>Sair da conta</span>
          </div>
          <div className="menu-item" onClick={excluirConta} style={{ padding:"15px 18px", cursor:"pointer", borderTop:`1px solid ${Z.cardBd}`, color:"#ef4444", fontSize:14 }}>
            Solicitar exclusão da conta e dos dados
          </div>
        </div>

        <p style={{ textAlign:"center", fontSize:11, color:Z.textMut, margin:"16px 0 0", animation:"fadeUp 0.4s ease 0.25s both" }}>
          Zuno v1.0.0 • Mobilidade para todos
        </p>
      </div>

      <BottomNav active="conta" />
    </div>
  );
}
